import {
  AfterViewInit,
  Component,
  TemplateRef,
  ViewChild,
} from "@angular/core";
import { Store } from "@ngrx/store";
import { SignatureInstallationState } from "../signature-installation-state.interface";
import { getCurrentStateSelector } from "../signature-installation-state.reducer";
import { MatSnackBar, MatSnackBarRef } from "@angular/material/snack-bar";
import { MatDialog } from "@angular/material/dialog";
import { SignatureInstallationErrorDialogComponent } from "../signature-installation-error-dialog/signature-installation-error-dialog.component";
import moment from "moment";
import { MY_FORMATS } from "../../shared/shared.module";
import { AuthService } from "../../auth/auth.service";

@Component({
  selector: "app-signature-installation-progress-toast",
  templateUrl: "./signature-installation-progress-toast.component.html",
  styleUrls: ["./signature-installation-progress-toast.component.scss"],
  standalone: false,
})
export class SignatureInstallationProgressToastComponent implements AfterViewInit {
  @ViewChild("snackbarTemplate") template: TemplateRef<unknown>;

  private snackbarRef: MatSnackBarRef<unknown> = null;
  private currentProcessId: string = null;
  private userClosed = false;

  public installedEmployeesCount = 0;
  public installedAliasesCount = 0;
  public totalInstallations = 0;
  public currentState: SignatureInstallationState;
  public lastUpdate: string;
  public hasLocalEmployees = false;

  constructor(
    private readonly store: Store,
    private readonly dialog: MatDialog,
    private readonly snackbar: MatSnackBar,
    private readonly authService: AuthService,
  ) {}

  ngAfterViewInit(): void {
    this.hasLocalEmployees = this.authService.dataSource === "LOCAL" || false;

    this.store.select(getCurrentStateSelector).subscribe((result) => {
      if (!result) {
        this.currentState = null;
        this.currentProcessId = null;
        this.installedEmployeesCount = 0;
        this.installedAliasesCount = 0;
        if (this.snackbarRef) {
          this.snackbarRef.dismiss();
          this.snackbarRef = null;
        }
        return;
      }

      if (
        this.currentProcessId &&
        this.currentProcessId !== result.signatureInstallationStateId
      ) {
        if (this.snackbarRef) {
          this.snackbarRef.dismiss();
          this.snackbarRef = null;
        }
        this.currentProcessId = result.signatureInstallationStateId;
        this.installedEmployeesCount = 0;
        this.installedAliasesCount = 0;
        this.userClosed = false;
      } else if (!this.currentProcessId) {
        this.currentProcessId = result.signatureInstallationStateId;
        this.userClosed = false;
      }

      this.currentState = result;
      this.lastUpdate = moment(new Date()).format(MY_FORMATS.display.time);

      const successfulInstallations = result.installations.filter(
        (i) => !i.error,
      );
      const newEmployeesCount = successfulInstallations.filter(
        (i) => !i.isAlias,
      ).length;
      const newAliasesCount = successfulInstallations.filter(
        (i) => i.isAlias,
      ).length;
      this.totalInstallations =
        (result.totalEmployees || 0) + (result.totalAliases || 0);

      if (result.status !== "IN_PROGRESS") {
        this.installedEmployeesCount = newEmployeesCount;
        this.installedAliasesCount = newAliasesCount;
      } else {
        if (newEmployeesCount !== this.installedEmployeesCount) {
          this.animateCounter(
            "installedEmployeesCount",
            this.installedEmployeesCount,
            newEmployeesCount,
            500,
          );
        }
        if (newAliasesCount !== this.installedAliasesCount) {
          this.animateCounter(
            "installedAliasesCount",
            this.installedAliasesCount,
            newAliasesCount,
            500,
          );
        }
      }

      if (!this.snackbarRef && !this.userClosed) {
        this.snackbarRef = this.snackbar.openFromTemplate(this.template);
      }
    });
  }

  private animateCounter(
    property: "installedEmployeesCount" | "installedAliasesCount",
    from: number,
    to: number,
    duration: number = 500,
  ): void {
    const steps = 20;
    const stepTime = duration / steps;
    const increment = (to - from) / steps;
    let currentStep = 0;
    const intervalId = setInterval(() => {
      currentStep++;
      this[property] = Math.round(from + increment * currentStep);
      if (currentStep >= steps) {
        clearInterval(intervalId);
        this[property] = to;
      }
    }, stepTime);
  }

  get errors(): SignatureInstallationState["installations"] {
    return this.currentState.installations.filter((i) => i.error);
  }

  close() {
    if (this.snackbarRef) {
      this.snackbarRef.dismiss();
      this.snackbarRef = null;
    }
    this.userClosed = true;
  }

  showErrors() {
    this.close();
    this.dialog.open(SignatureInstallationErrorDialogComponent, {
      panelClass: "app-dialog-panel",
      data: this.currentState,
    });
  }
}
